import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Flame, Star, BookOpen, Zap, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/components/DashboardLayout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  { value: 'Telugu', label: 'తెలుగు (Telugu)', icon: '📚' },
  { value: 'Hindi', label: 'हिंदी (Hindi)', icon: '📖' },
  { value: 'English', label: 'English', icon: '📝' },
  { value: 'Mathematics', label: 'Mathematics', icon: '🔢' },
  { value: 'Social', label: 'Social Studies', icon: '🌍' },
  { value: 'Physics', label: 'Physics', icon: '⚛️' },
  { value: 'Biology', label: 'Biology', icon: '🧬' }
];

const MARKS_TYPES = ['All', '1M', '2M', '4M', '8M'];

export default function ImportantQuestions() {
  const { getAuthHeaders } = useAuth();
  const [formData, setFormData] = useState({
    subject: 'Mathematics',
    chapter: '',
    marks_type: 'All',
    num_questions: 15
  });
  const [chapters, setChapters] = useState([]);
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChapters(formData.subject);
  }, [formData.subject]);

  const fetchChapters = async (subject) => {
    try {
      const response = await axios.get(`${API}/subjects/chapters?subject=${subject}`, {
        headers: getAuthHeaders()
      });
      setChapters(response.data.chapters);
      setFormData(prev => ({ ...prev, chapter: '' }));
    } catch (error) {
      console.error('Failed to fetch chapters');
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setQuestions(null);

    try {
      const params = new URLSearchParams({
        subject: formData.subject,
        num_questions: formData.num_questions.toString()
      });
      
      if (formData.chapter) params.append('chapter', formData.chapter);
      if (formData.marks_type !== 'All') params.append('marks_type', formData.marks_type);

      const response = await axios.post(
        `${API}/important-questions/generate?${params.toString()}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setQuestions(response.data);
      toast.success('Important questions generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setLoading(false);
    }
  };

  const getImportanceIcon = (importance) => {
    if (importance === 'Very Important') return <Flame className="w-5 h-5 text-red-500" />;
    if (importance === 'Important') return <Star className="w-5 h-5 text-yellow-500" />;
    return <BookOpen className="w-5 h-5 text-blue-500" />;
  };

  const getImportanceColor = (importance) => {
    if (importance === 'Very Important') return 'bg-red-50 border-red-200 text-red-700';
    if (importance === 'Important') return 'bg-yellow-50 border-yellow-200 text-yellow-700';
    return 'bg-blue-50 border-blue-200 text-blue-700';
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 fade-in">
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200 p-8">
          <div className="flex items-center space-x-3">
            <Flame className="w-10 h-10 text-red-600" />
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Important Questions</h1>
              <p className="text-slate-600">High-weightage questions from previous years & model papers</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-8">
          <form onSubmit={handleGenerate} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">
                  Subject
                </label>
                <select
                  id="subject"
                  data-testid="important-subject-select"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  {SUBJECTS.map((subject) => (
                    <option key={subject.value} value={subject.value}>
                      {subject.icon} {subject.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="chapter" className="block text-sm font-medium text-slate-700 mb-2">
                  Chapter/Topic (Optional)
                </label>
                <select
                  id="chapter"
                  data-testid="important-chapter-select"
                  value={formData.chapter}
                  onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  <option value="">All Chapters</option>
                  {chapters.map((chapter, idx) => (
                    <option key={idx} value={chapter}>{chapter}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="marks_type" className="block text-sm font-medium text-slate-700 mb-2">
                  Marks Type
                </label>
                <select
                  id="marks_type"
                  data-testid="important-marks-select"
                  value={formData.marks_type}
                  onChange={(e) => setFormData({ ...formData, marks_type: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                >
                  {MARKS_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="num_questions" className="block text-sm font-medium text-slate-700 mb-2">
                  Number of Questions
                </label>
                <input
                  id="num_questions"
                  data-testid="important-num-input"
                  type="number"
                  min="5"
                  max="30"
                  value={formData.num_questions}
                  onChange={(e) => setFormData({ ...formData, num_questions: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-50 border border-transparent rounded-md focus:bg-white focus:border-blue-900 focus:ring-0"
                />
              </div>
            </div>

            <Button
              data-testid="generate-important-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg btn-primary"
            >
              <Flame className="w-5 h-5 mr-2" />
              {loading ? 'Generating...' : 'Generate Important Questions'}
            </Button>
          </form>
        </div>

        {questions && (
          <div data-testid="important-questions-display" className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    {questions.subject} - Important Questions
                  </h2>
                  {questions.chapter && (
                    <p className="text-slate-600 mt-1">{questions.chapter}</p>
                  )}
                </div>
                <span className="marks-badge bg-red-50 text-red-700 px-4 py-2 rounded-full">
                  {questions.questions.length} Questions
                </span>
              </div>

              <div className="space-y-4">
                {questions.questions.map((q, index) => (
                  <div
                    key={index}
                    className="p-6 bg-slate-50 rounded-md border border-slate-200 hover:shadow-md transition-shadow"
                    data-testid={`important-q-${index}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        {getImportanceIcon(q.importance)}
                        <span className={`text-xs font-medium px-2 py-1 rounded-full border ${getImportanceColor(q.importance)}`}>
                          {q.importance}
                        </span>
                        <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded-full">
                          {q.type}
                        </span>
                      </div>
                      <span className="marks-badge bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                        {q.marks}M
                      </span>
                    </div>
                    <p className="text-slate-900 font-medium leading-relaxed">
                      <span className="text-blue-900 font-bold">Q{index + 1}.</span> {q.question}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg border border-orange-200 p-6">
              <h3 className="font-bold text-orange-900 mb-3 flex items-center">
                <Zap className="w-5 h-5 mr-2" />
                Exam Strategy
              </h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li>• 🔥 <strong>Very Important</strong>: Practice these questions multiple times</li>
                <li>• ⭐ <strong>Important</strong>: Cover these for good marks</li>
                <li>• 📘 <strong>Core Questions</strong>: Fundamental concepts from textbook</li>
                <li>• Focus on questions marked as "Very Important" for quick revision</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
